import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SectionCard, StatusChip, EmptyState } from '../../../../components/ui';
import { brandHome } from '../../brandHomeTokens';
import { SEVERITY_TONES, severityToTone } from '../../severityTones';
import type { VendorConnector, IngestionJobRow, IngestionSummary } from '../../useBrandHomeData';

interface IngestionFeedCardProps {
  connector: VendorConnector | null;
  jobs: IngestionJobRow[];
  summary: IngestionSummary | null;
  loading?: boolean;
}

export const IngestionFeedCard: React.FC<IngestionFeedCardProps> = ({ connector, jobs, summary, loading }) => {
  const isActive = connector?.status === 'active';
  const last10 = jobs.slice(0, 10);

  return (
    <SectionCard
      dense
      title="Your ingestion feed"
      actions={
        <StatusChip
          label={connector ? (isActive ? 'Active' : connector.status) : 'No feed'}
          tone={severityToTone(isActive ? 'positive' : 'blocking')}
        />
      }
    >
      {loading ? (
        <Box sx={{ p: '15px 18px' }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rectangular" height={80} sx={{ mt: 1, borderRadius: 1 }} />
        </Box>
      ) : !connector ? (
        <EmptyState size="compact" title="No ingestion feed configured yet." />
      ) : (
        <Box sx={{ p: '15px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{connector.connectorName || 'Ingestion feed'}</Typography>
            {connector.handler?.endpoint && (
              <Typography sx={{ fontSize: '12px', color: brandHome.color.muted, fontFamily: brandHome.font.mono, overflowWrap: 'anywhere' }}>
                {connector.handler.endpoint}
              </Typography>
            )}
          </Box>
          {(connector.dataDomains?.length ?? 0) > 0 && (
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {connector.dataDomains!.map((tag) => (
                <Box key={tag} component="span" sx={{ px: '9px', py: '3px', borderRadius: `${brandHome.radius.chip}px`, bgcolor: brandHome.color.chipFill, color: brandHome.color.bodyAlt, fontSize: '11.5px', fontWeight: 600 }}>
                  {tag}
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <FactRow label="Schedule" value={connector.schedule?.frequency || 'manual'} />
            <FactRow label="Last run" value={connector.schedule?.lastRun ? new Date(connector.schedule.lastRun).toLocaleString() : '—'} />
            <FactRow label="Next run" value={connector.schedule?.nextRun ? new Date(connector.schedule.nextRun).toLocaleString() : '—'} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: brandHome.color.faint }}>
                Last 10 runs
              </Typography>
              {summary && (
                <Typography
                  sx={{
                    ml: 'auto',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: summary.successRate >= 90 ? brandHome.color.positiveInk : brandHome.color.cautionInk,
                  }}
                >
                  {summary.successRate}% success
                </Typography>
              )}
            </Box>
            {last10.length === 0 ? (
              <EmptyState size="compact" title="No runs recorded yet." />
            ) : (
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {last10.map((job) => (
                  <Box
                    key={job.id}
                    title={job.status}
                    sx={{ flex: '1 1 0', height: 26, borderRadius: '4px', bgcolor: job.status === 'completed' ? SEVERITY_TONES.positive.dot : job.status === 'failed' ? SEVERITY_TONES.blocking.dot : brandHome.color.faint }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </SectionCard>
  );
};

const FactRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', borderBottom: `1px dashed ${brandHome.color.hairline}`, pb: '7px' }}>
    <Typography sx={{ fontSize: '12px', color: brandHome.color.faint, flex: '0 0 108px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{value}</Typography>
  </Box>
);
